/**
 * Data access for the insurance estimate.
 *
 * Its own file rather than more of `queries.ts` because it is a self-contained
 * document with four tables of its own and touches nothing else — which is the
 * point of the feature (ADR 0055). Keeping the queries apart is the cheapest
 * way to keep that true: there is no join here that could quietly pull a claim
 * sheet into the money the company runs on.
 */

import type { ClaimContact } from "./printInsuranceEstimate";
import type { CompanySettingsRow } from "./queries";
import { supabase, unwrap } from "./supabase";

/** The estimate row: one per job, carrying the settings every line reads. */
export interface InsuranceEstimateRow {
  id: string;
  job_id: string;
  name: string;
  carrier: string | null;
  claim_number: string | null;
  date_of_loss: string | null;
  tax_rate: number | string;
  default_material_pct: number | string;
  overhead_pct: number | string;
  profit_pct: number | string;
  apply_overhead_and_profit: boolean;
  roof_type: string;
}

export interface InsuranceAreaRow {
  id: string;
  insurance_estimate_id: string;
  name: string;
  sort_order: number;
}

export interface InsuranceItemRow {
  id: string;
  area_id: string;
  description: string;
  note: string | null;
  qty: number | string;
  unit: string;
  remove_price: number | string;
  replace_price: number | string;
  material_pct: number | string | null;
  sort_order: number;
}

/** A line the rep has typed before, offered when adding a new one. */
export interface InsuranceLibraryRow {
  id: string;
  description: string;
  note: string | null;
  unit: string;
  remove_price: number | string;
  replace_price: number | string;
  material_pct: number | string | null;
  category: string | null;
  times_used: number;
}

export interface InsuranceEstimateBundle {
  estimate: InsuranceEstimateRow;
  areas: InsuranceAreaRow[];
  items: InsuranceItemRow[];
}

/**
 * The job's insurance estimate, or null if it has never been started.
 *
 * Null is a real state and not an error: the card offers to start one, which
 * is the toggle Jonathon asked for. Nothing is written until he does.
 */
export async function fetchInsuranceEstimate(
  jobId: string,
): Promise<InsuranceEstimateBundle | null> {
  const { data, error } = await supabase
    .from("insurance_estimates")
    .select("*")
    .eq("job_id", jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const estimate = data as InsuranceEstimateRow | null;
  if (!estimate) return null;

  const areas = unwrap<InsuranceAreaRow[]>(
    await supabase
      .from("insurance_estimate_areas")
      .select("*")
      .eq("insurance_estimate_id", estimate.id)
      .order("sort_order")
      .order("created_at"),
  );

  const items = areas.length
    ? unwrap<InsuranceItemRow[]>(
        await supabase
          .from("insurance_estimate_items")
          .select("*")
          .in(
            "area_id",
            areas.map((a) => a.id),
          )
          .order("sort_order")
          .order("created_at"),
      )
    : [];

  return { estimate, areas, items };
}

/**
 * Start one, with the first area already on it.
 *
 * "Dwelling" because every reference estimate starts there and a rep who has
 * to name a room before typing a line has been given homework instead of a
 * form. It renames like any other area.
 */
export async function startInsuranceEstimate(
  jobId: string,
  defaults: Partial<InsuranceEstimateRow> = {},
): Promise<InsuranceEstimateBundle> {
  const { data, error } = await supabase
    .from("insurance_estimates")
    .insert({ job_id: jobId, ...defaults })
    .select()
    .single();
  if (error) throw new Error(error.message);
  const estimate = data as InsuranceEstimateRow;

  await addInsuranceArea(estimate.id, "Dwelling", 100);
  return (await fetchInsuranceEstimate(jobId))!;
}

export async function addInsuranceArea(
  estimateId: string,
  name: string,
  sortOrder: number,
): Promise<InsuranceAreaRow> {
  const { data, error } = await supabase
    .from("insurance_estimate_areas")
    .insert({
      insurance_estimate_id: estimateId,
      name,
      sort_order: sortOrder,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as InsuranceAreaRow;
}

/**
 * Append a roof type's usual scope to an area.
 *
 * Through the database function so the screen and any MCP tool insert the same
 * lines at the same prices. Returns how many arrived, which is what the card
 * tells the rep — "14 lines added" is the difference between a button that
 * worked and one that looked like it did nothing.
 */
export async function applyRoofTemplate(
  areaId: string,
  roofType: string,
): Promise<number> {
  const { data, error } = await supabase.rpc("apply_insurance_roof_template", {
    p_area_id: areaId,
    p_roof_type: roofType,
  });
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

export async function addInsuranceItem(
  areaId: string,
  row: Partial<InsuranceItemRow>,
  sortOrder: number,
): Promise<InsuranceItemRow> {
  const { data, error } = await supabase
    .from("insurance_estimate_items")
    .insert({ area_id: areaId, sort_order: sortOrder, ...row })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as InsuranceItemRow;
}

/**
 * The lines this company has typed before, most-reached-for first.
 *
 * Capped: the picker is a list somebody scans, and a rep who has typed six
 * hundred lines wants the twenty they use, not all of them. The search box
 * filters what is loaded, which is enough at this size.
 */
export async function fetchInsuranceLibrary(): Promise<InsuranceLibraryRow[]> {
  return unwrap<InsuranceLibraryRow[]>(
    await supabase
      .from("insurance_line_items")
      .select("*")
      .eq("active", true)
      .order("times_used", { ascending: false })
      .order("last_used_at", { ascending: false })
      .limit(400),
  );
}

/**
 * Remember a line, so the next claim starts from a list.
 *
 * Through the database function rather than an upsert from here, so that the
 * case-insensitive matching and the use count are the same for anything else
 * that ever writes a line — an MCP tool included.
 *
 * Deliberately best-effort: this is called as a rep leaves a field, and a
 * library that fails to learn must never be what stops them typing. The caller
 * ignores the rejection.
 */
export async function rememberInsuranceLine(
  row: Pick<
    InsuranceItemRow,
    "description" | "unit" | "remove_price" | "replace_price" | "material_pct"
  > & { note?: string | null; category?: string | null },
): Promise<void> {
  const { error } = await supabase.rpc("remember_insurance_line", {
    p_description: row.description,
    p_unit: row.unit,
    p_remove_price: Number(row.remove_price) || 0,
    p_replace_price: Number(row.replace_price) || 0,
    p_material_pct:
      row.material_pct === null || row.material_pct === ""
        ? null
        : Number(row.material_pct),
    p_note: row.note ?? null,
    p_category: row.category ?? null,
  });
  if (error) throw new Error(error.message);
}

/**
 * The one person printed on a claim sheet, and the company details beside them.
 *
 * One round trip for both, because the print button needs them together and
 * neither changes often. The profile is reached through
 * `company_settings.claim_contact_profile_id` rather than by name, so who is on
 * the document is a row edit — see ADR 0055.
 */
export async function fetchClaimBranding(): Promise<{
  settings: CompanySettingsRow | null;
  contact: ClaimContact | null;
}> {
  const { data, error } = await supabase
    .from("company_settings")
    .select(
      "*, claim_contact:profiles!company_settings_claim_contact_profile_id_fkey(full_name,phone,email)",
    )
    .eq("id", 1)
    .maybeSingle();
  if (error) throw new Error(error.message);

  const row = data as
    (CompanySettingsRow & { claim_contact: ClaimContact | null }) | null;
  return {
    settings: row,
    contact: row?.claim_contact ?? null,
  };
}
